import React, { useState, useEffect, useCallback } from 'react';
import { GoogleMap, Polyline, InfoWindow } from '@react-google-maps/api';
import { useGoogleMaps, GOOGLE_MAPS_MAP_ID } from '../../lib/googleMaps';
import AdvancedMarker from '../../components/maps/AdvancedMarker';
import api from '../../lib/api';
import { useTranslation } from 'react-i18next';
import { NavigationArrow, MapPin, Clock, Path, Phone, House } from '@phosphor-icons/react';

const MAP_STYLES = [
  { elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#c9c9c9' }] },
];

const WAYPOINT_COLORS = { pickup: '#00C853', drop: '#FF3333', office: '#002FA7' };

export default function PilotLiveRoute() {
  const { isLoaded } = useGoogleMaps();
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedWP, setSelectedWP] = useState(null);

  const fetchRoute = useCallback(async () => {
    try {
      const { data: d } = await api.get('/pilot/live-route');
      setData(d);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchRoute();
    const interval = setInterval(fetchRoute, 5000);
    return () => clearInterval(interval);
  }, [fetchRoute]);

  if (!isLoaded || loading) return <div className="h-[400px] bg-[#F3F3F3] flex items-center justify-center text-[#666] text-sm">Loading route...</div>;
  if (!data) return null;

  const { pilot_location, waypoints, active_trips, total_km } = data;
  const pilotPos = pilot_location ? { lat: pilot_location.lat, lng: pilot_location.lng } : null;

  // Build route path
  const routePoints = [];
  if (pilotPos) routePoints.push(pilotPos);
  waypoints.forEach(wp => routePoints.push({ lat: wp.lat, lng: wp.lng }));

  const center = pilotPos || (waypoints.length > 0 ? { lat: waypoints[0].lat, lng: waypoints[0].lng } : { lat: 13.0827, lng: 80.2707 });
  const nextWP = waypoints[0];

  return (
    <div data-testid="pilot-live-route" className="space-y-4">
      {/* ETA Banner */}
      {nextWP && (
        <div className="bg-[#002FA7] text-white p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <NavigationArrow size={24} weight="bold" className="animate-pulse-dot" />
            <div>
              <p className="text-xs uppercase tracking-widest text-white/60">Next Stop</p>
              <p className="text-lg font-bold">{nextWP.label}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-3xl font-black">{nextWP.eta_minutes} min</p>
            <p className="text-xs text-white/60">{nextWP.distance_km} km away</p>
          </div>
        </div>
      )}

      {/* Map */}
      <div className="border border-[#E5E5E5]" style={{ height: '350px' }}>
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          center={center}
          zoom={13}
          options={{ styles: MAP_STYLES, disableDefaultUI: true, zoomControl: true, mapId: GOOGLE_MAPS_MAP_ID }}
        >
          {/* Pilot position */}
          {pilotPos && (
            <AdvancedMarker
              position={pilotPos}
              icon={{ color: '#002FA7', label: 'ME', size: 38 }}
              title="You are here"
              zIndex={10}
            />
          )}

          {/* Waypoint markers */}
          {waypoints.map((wp, i) => (
            <AdvancedMarker
              key={`${wp.type}-${wp.trip_id || i}`}
              position={{ lat: wp.lat, lng: wp.lng }}
              onClick={() => setSelectedWP(wp)}
              icon={{ color: WAYPOINT_COLORS[wp.type] || '#808080', label: `${i + 1}`, size: 28 }}
              title={wp.label}
            />
          ))}

          {/* Route line */}
          {routePoints.length >= 2 && (
            <Polyline path={routePoints} options={{ strokeColor: '#002FA7', strokeWeight: 4, strokeOpacity: 0.85 }} />
          )}

          {/* Info window */}
          {selectedWP && (
            <InfoWindow position={{ lat: selectedWP.lat, lng: selectedWP.lng }} onCloseClick={() => setSelectedWP(null)}>
              <div className="p-1 min-w-[140px]">
                <p className="font-bold text-sm">{selectedWP.label}</p>
                <p className="text-xs text-gray-500">ETA: {selectedWP.eta_minutes} min ({selectedWP.distance_km} km)</p>
                {selectedWP.contact && <p className="text-xs mt-1"><a href={`tel:${selectedWP.contact}`} className="text-blue-600">{selectedWP.contact}</a></p>}
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
      </div>

      {/* Waypoint list (like Uber's trip queue) */}
      <div className="bg-white border border-[#E5E5E5]">
        {waypoints.map((wp, i) => {
          const isNext = i === 0;
          return (
            <div key={`${wp.type}-${wp.trip_id || i}`} className={`flex items-center gap-3 p-3 border-b border-[#E5E5E5] last:border-0 ${isNext ? 'bg-[#002FA7]/5' : ''}`}>
              {/* Dot & line */}
              <div className="flex flex-col items-center w-6">
                <div className={`w-4 h-4 rounded-full flex items-center justify-center text-white text-[9px] font-bold`} style={{ backgroundColor: WAYPOINT_COLORS[wp.type] || '#808080' }}>
                  {i + 1}
                </div>
                {i < waypoints.length - 1 && <div className="w-px h-6 bg-[#E5E5E5]" />}
              </div>
              {/* Info */}
              <div className="flex-1 min-w-0">
                <p className={`text-sm ${isNext ? 'font-bold text-[#002FA7]' : 'font-medium'}`}>{wp.label}</p>
                <div className="flex items-center gap-3 text-xs text-[#666] mt-0.5">
                  <span className="flex items-center gap-1"><Clock size={12} />{wp.eta_minutes} min</span>
                  <span className="flex items-center gap-1"><Path size={12} />{wp.distance_km} km</span>
                </div>
              </div>
              {/* Contact */}
              {wp.contact && (
                <a href={`tel:${wp.contact}`} className="p-2 text-[#002FA7]"><Phone size={18} /></a>
              )}
              {/* Type badge */}
              <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 ${wp.type === 'pickup' ? 'bg-[#00C853]/10 text-[#00C853]' : wp.type === 'drop' ? 'bg-[#FF3333]/10 text-[#FF3333]' : 'bg-[#002FA7]/10 text-[#002FA7]'}`}>
                {wp.type === 'office' ? 'Office' : wp.type}
              </span>
            </div>
          );
        })}
        {waypoints.length === 0 && (
          <div className="p-6 text-center text-[#666] text-sm">
            <MapPin size={32} className="mx-auto mb-2 text-[#E5E5E5]" />
            No active route. Accept a trip to see your route.
          </div>
        )}
      </div>
    </div>
  );
}
